/**
 * BulkEditAction — «✏️ Editar campos» de la barra de selección de los listados.
 *
 * QUÉ RESUELVE
 * ---------------------------------------------------------------------------
 * Hasta ahora la barra de selección solo sabía asignar ubicación y rack
 * (`BulkPlacementAction`). Cambiar el fabricante de 20 servidores, el entorno
 * de una docena de VMs o la garantía de las SAIs seguía siendo ficha a ficha.
 * Este diálogo es el «mass editing» de Odoo: se marcan los campos que se
 * quieren tocar y se escribe su nuevo valor; SOLO se envía lo marcado.
 *
 * LÍMITES (a propósito)
 * ---------------------------------------------------------------------------
 * Los campos disponibles salen de `utils/bulkEditFields.js`, que es espejo de
 * la whitelist del backend. No hay nombre/código/serie/MAC/IP (son únicos) ni
 * ubicación/rack (tiene su propio botón con validación de hueco y sede).
 *
 * El resumen se pinta como diálogo aparte y se cierra ANTES de avisar al
 * listado: si se avisara al aplicar, la selección se vacía, `bulkActions`
 * deja de pintarse y este componente se desmontaría con el resumen dentro
 * (el mismo gotcha de `BulkPlacementAction`).
 */
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { bulkFields } from '../../utils/bulkEditFields';
import Dialog from './Dialog';

function defaultFor(field) {
  if (field.type === 'bool') return false;
  if (field.type === 'select') return field.options?.[0]?.value ?? '';
  return '';
}

export default function BulkEditAction({
  companyId,
  entity,
  selectedRows = [],
  onApplied,
  label,
}) {
  const { t } = useTranslation();
  const tr = (key, fallback) => {
    const value = t(key);
    return !value || value === key ? fallback : value;
  };

  const fields = useMemo(() => bulkFields(entity), [entity]);

  const [rows, setRows] = useState(null);          // selección congelada al abrir
  const [checked, setChecked] = useState({});      // campo → marcado
  const [values, setValues] = useState({});        // campo → valor
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const open = () => {
    const initialValues = {};
    fields.forEach((field) => { initialValues[field.key] = defaultFor(field); });
    setValues(initialValues);
    setChecked({});
    setError('');
    setRows(selectedRows);
  };

  const close = () => {
    if (saving) return;
    setRows(null);
    setChecked({});
    setValues({});
    setError('');
  };

  const closeResult = () => {
    const payload = result;
    setResult(null);
    onApplied?.(payload);
  };

  const toggle = (key) => setChecked((prev) => ({ ...prev, [key]: !prev[key] }));
  const setValue = (key, value) => setValues((prev) => ({ ...prev, [key]: value }));

  const chosenKeys = Object.keys(checked).filter((key) => checked[key]);

  const submit = async () => {
    if (!chosenKeys.length) {
      setError(tr('bulk_edit.nothing', 'Marca al menos un campo para cambiarlo.'));
      return;
    }
    const payload = {};
    chosenKeys.forEach((key) => {
      const field = fields.find((item) => item.key === key);
      const value = values[key];
      if (field?.type === 'bool') payload[key] = Boolean(value);
      else payload[key] = value ?? '';
    });

    setSaving(true);
    setError('');
    try {
      const response = await api.bulkUpdateFields(
        entity,
        (rows || []).map((row) => row.id),
        payload,
        companyId,
      );
      setRows(null);
      setResult(response || { updated: 0, failed: [] });
    } catch (e) {
      setError(e.message || tr('bulk_edit.error', 'No se pudo aplicar el cambio'));
    } finally {
      setSaving(false);
    }
  };

  const names = (rows || []).slice(0, 4).map((row) => row.name || row.code || `#${row.id}`);
  const more = (rows?.length || 0) - names.length;

  if (!fields.length) return null;

  return (
    <>
      <button
        type="button"
        className="list-btn-secondary"
        onClick={open}
        disabled={!selectedRows.length}
        title={tr('bulk_edit.open_hint', 'Cambiar un campo en todos los seleccionados')}
      >
        <span aria-hidden="true">✏️</span> {label || tr('bulk_edit.open', 'Editar campos')}
      </button>

      {rows && (
        <Dialog
          title={tr('bulk_edit.title', 'Edición masiva de campos')}
          icon="✏️"
          onClose={close}
          busy={saving}
          maxWidth={560}
          className="bulk-edit-dialog"
          footer={(
            <>
              <button type="button" className="list-btn-secondary" onClick={close} disabled={saving}>
                {tr('action.cancel', 'Cancelar')}
              </button>
              <button
                type="button"
                className="list-btn-primary"
                onClick={submit}
                disabled={saving || !chosenKeys.length}
              >
                {saving
                  ? tr('bulk.applying', 'Aplicando…')
                  : tr('bulk.apply', 'Aplicar a los seleccionados')}
              </button>
            </>
          )}
        >
          <p className="list-hint" style={{ marginTop: 0 }}>
            <strong>{rows.length}</strong>{' '}
            {rows.length === 1
              ? tr('bulk.row_selected', 'elemento seleccionado')
              : tr('bulk.rows_selected', 'elementos seleccionados')}
            {names.length > 0 && (
              <> · {names.join(', ')}{more > 0 ? ` ${tr('bulk.and_more', 'y')} ${more} ${tr('bulk.more', 'más')}` : ''}</>
            )}
          </p>

          <p className="list-hint">
            {tr(
              'bulk_edit.hint',
              'Marca solo los campos que quieras cambiar. Los identificadores (nombre, código, nº de serie, MAC, IP) no se cambian en masa y la ubicación tiene su propio botón.',
            )}
          </p>

          <div className="bulk-edit-fields">
            {fields.map((field) => {
              const on = Boolean(checked[field.key]);
              return (
                <div key={field.key} className={`bulk-edit-field${on ? ' is-on' : ''}`}>
                  <label className="bulk-edit-check">
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={() => toggle(field.key)}
                      disabled={saving}
                    />
                    <span>{field.label}</span>
                  </label>
                  {on && (
                    field.type === 'select' ? (
                      <select
                        className="list-select"
                        value={values[field.key] ?? ''}
                        onChange={(event) => setValue(field.key, event.target.value)}
                        disabled={saving}
                        aria-label={field.label}
                      >
                        {(field.options || []).map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    ) : field.type === 'bool' ? (
                      <label className="bulk-edit-switch">
                        <input
                          type="checkbox"
                          checked={Boolean(values[field.key])}
                          onChange={(event) => setValue(field.key, event.target.checked)}
                          disabled={saving}
                        />
                        <span>{values[field.key] ? tr('common.yes', 'Sí') : tr('common.no', 'No')}</span>
                      </label>
                    ) : (
                      <input
                        className="list-input"
                        type={field.type === 'date' ? 'date' : 'text'}
                        value={values[field.key] ?? ''}
                        onChange={(event) => setValue(field.key, event.target.value)}
                        disabled={saving}
                        aria-label={field.label}
                        placeholder={tr('bulk_edit.empty_clears', 'Vacío = borrar el valor')}
                      />
                    )
                  )}
                </div>
              );
            })}
          </div>

          {error && (
            <p className="bulk-grid-error" role="alert">{error}</p>
          )}
        </Dialog>
      )}

      {result && (
        <Dialog
          title={tr('bulk.done_title', 'Cambio masivo aplicado')}
          icon={result.failed?.length ? '⚠️' : '✅'}
          onClose={closeResult}
          maxWidth={520}
          footer={(
            <button type="button" className="list-btn-primary" onClick={closeResult}>
              {tr('action.close', 'Cerrar')}
            </button>
          )}
        >
          <p>
            <strong>{result.updated}</strong>{' '}
            {result.updated === 1
              ? tr('bulk.done_row', 'elemento actualizado')
              : tr('bulk.done_rows', 'elementos actualizados')}
            {result.failed?.length > 0 && (
              <> · <strong style={{ color: 'var(--red-600)' }}>{result.failed.length}</strong>{' '}
                {result.failed.length === 1
                  ? tr('bulk.done_failed_one', 'no se pudo cambiar')
                  : tr('bulk.done_failed', 'no se pudieron cambiar')}</>
            )}
          </p>
          {result.failed?.length > 0 && (
            <ul className="list-hint" style={{ marginTop: 8, paddingLeft: 18 }}>
              {result.failed.slice(0, 10).map((item) => (
                <li key={item.id}>#{item.id}: {item.error}</li>
              ))}
              {result.failed.length > 10 && <li>…</li>}
            </ul>
          )}
        </Dialog>
      )}
    </>
  );
}
